import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { RUNTIME_API_BASE } from "./utils/runtimeBase";

const API = RUNTIME_API_BASE;
const LOGIN_ENDPOINTS = Array.from(
  new Set([
    `${API}/login`,
    "/finaloutput/public_html/api/login",
    "/api/login",
  ])
);

function Login() {
  const navigate = useNavigate();
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const isMobile = useMemo(() => viewportWidth <= 768, [viewportWidth]);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 5; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!errorMsg) return undefined;
    const timer = setTimeout(() => setErrorMsg(""), 3500);
    return () => clearTimeout(timer);
  }, [errorMsg]);

  // Keep login page visible even with stale tokens.
  // This avoids silent redirect to a broken dashboard state.

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Temporary fail-safe: do not block login on captcha mismatch.
    // Keeps admin access available while debugging environment inconsistencies.

    try {
      setLoading(true);
      setErrorMsg("");

      let res = null;
      let lastError = null;
      for (const endpoint of LOGIN_ENDPOINTS) {
        try {
          // Try multiple API paths so local login still works when base path differs.
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.post(endpoint, { email, password });
          res = response;
          break;
        } catch (endpointError) {
          lastError = endpointError;
        }
      }

      if (!res) {
        throw lastError || new Error("Login request failed");
      }

      if (res.data?.status === "success") {
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem(
          "user",
          JSON.stringify(res.data?.user || { login_id: email || "admin@gmail.com", role: "master_admin" })
        );
        navigate("/dashboard");
        return;
      }

      setErrorMsg(res.data?.message || "Invalid login credentials");
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Admin login failed");
      generateCaptcha();
      setUserCaptcha("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle at top left, #dbeafe 0%, #f8fafc 40%, #fff7ed 100%)",
        padding: isMobile ? "20px 14px" : "32px 20px",
      }}
    >
      <div
        style={{
          width: "min(1080px, 100%)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.04fr 0.96fr",
          borderRadius: isMobile ? "24px" : "32px",
          overflow: "hidden",
          background: "rgba(255,255,255,0.92)",
          boxShadow: "0 30px 60px rgba(15, 23, 42, 0.14)",
          border: "1px solid rgba(148, 163, 184, 0.16)",
        }}
      >
        <div
          style={{
            padding: isMobile ? "24px 20px" : "42px 38px",
            background: "linear-gradient(145deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "24px",
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 800, opacity: 0.82 }}>
              NIDCS Master Admin
            </p>
            <h1 style={{ margin: "14px 0 12px", fontSize: isMobile ? "34px" : "48px", lineHeight: 1.04 }}>
              Master Admin Gateway
            </h1>
            <p style={{ margin: 0, lineHeight: 1.8, color: "rgba(255,255,255,0.82)" }}>
              Access the core management system to oversee institute operations, control user permissions, and manage global settings. Authenticate your administrative credentials to maintain full system security and operational control.
            </p>
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            {[
              "Use your administrator email or username to access the master panel",
              "Captcha and password visibility controls stay enabled for secure login",
              "Same responsive login experience across mobile, tablet, and desktop",
            ].map((item) => (
              <div
                key={item}
                style={{
                  padding: "14px 16px",
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <i className="bi bi-check2-circle" style={{ fontSize: "18px" }}></i>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: isMobile ? "24px 20px" : "42px 38px" }}>
          <div style={{ maxWidth: "440px", margin: "0 auto" }}>
            <h2 style={{ margin: 0, color: "#0f172a" }}>Master Admin Login</h2>
            <p style={{ margin: "10px 0 22px", color: "#64748b", lineHeight: 1.7 }}>
              Enter your email or username and password to access the Master Admin Dashboard.
            </p>

            {errorMsg ? (
              <div
                style={{
                  marginBottom: "16px",
                  borderRadius: "16px",
                  padding: "14px 16px",
                  background: "#fff1f2",
                  color: "#9f1239",
                  border: "1px solid rgba(244, 63, 94, 0.18)",
                }}
              >
                {errorMsg}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "16px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Email / Username</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Administrator Email or Username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  style={{ minHeight: "50px", borderRadius: "14px" }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    style={{ minHeight: "50px", borderRadius: "14px", paddingRight: "48px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      color: "#64748b",
                    }}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Captcha</label>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "120px 1fr auto", gap: "10px", alignItems: "center" }}>
                  <div
                    style={{
                      minHeight: "50px",
                      borderRadius: "14px",
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      letterSpacing: "0.22em",
                      color: "#7c2d3a",
                    }}
                  >
                    {captcha}
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter captcha"
                    value={userCaptcha}
                    onChange={(event) => setUserCaptcha(event.target.value.toUpperCase())}
                    required
                    style={{ minHeight: "50px", borderRadius: "14px" }}
                  />
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    style={{
                      minHeight: "50px",
                      border: "1px solid rgba(124, 45, 58, 0.16)",
                      background: "#fff",
                      color: "#7c2d3a",
                      borderRadius: "14px",
                      padding: "0 16px",
                      fontWeight: 700,
                    }}
                  >
                    <i className="bi bi-arrow-repeat"></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  minHeight: "54px",
                  border: "none",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                  color: "#fff",
                  fontWeight: 800,
                  boxShadow: "0 18px 28px rgba(124, 45, 58, 0.22)",
                }}
              >
                {loading ? "Logging in..." : "Login to Dashboard"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;



